import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
    Wrench, Calendar, AlertTriangle, CheckCircle, Clock, Shield,
    Plus, RefreshCw, Settings, ChevronDown, ChevronUp, Plane, Trash2, PenTool
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const alertStyles = {
    overdue: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/40', label: 'VENCIDO', dot: 'bg-red-500' },
    warning: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/40', label: 'ALERTA', dot: 'bg-amber-500' },
    caution: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/40', label: 'PRECAUCION', dot: 'bg-yellow-500' },
    ok: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/40', label: 'OK', dot: 'bg-emerald-500' },
};

const checkColors = { A: '#3b82f6', B: '#8b5cf6', C: '#f59e0b', D: '#ef4444', OOP: '#06b6d4' };

function ProgressBar({ value, max, color, label, unit }) {
    if (!max || max <= 0) return null; // OOP tasks skip metrics with interval=0
    const pct = max > 0 ? Math.min(100, Math.round((1 - value / max) * 100)) : 0;
    const barColor = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
    return (
        <div className="space-y-1">
            <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>{label}</span>
                <span className="font-mono">{value} {unit} restantes</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function CheckCard({ check, onRecordCheck, onGenerateWO, canManage }) {
    const style = alertStyles[check.alert_level] || alertStyles.ok;
    const isOOP = check.check_type === 'OOP';
    const badgeLabel = isOOP ? 'OOP' : check.check_type;
    const canGenerateWO = check.alert_level !== 'ok';
    return (
        <div className={`rounded-lg border ${style.border} p-3 space-y-2`} data-testid={`check-${check.check_type}${isOOP ? '-' + check.program_id : ''}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs"
                         style={{ backgroundColor: checkColors[check.check_type] + '20', color: checkColors[check.check_type] }}>
                        {badgeLabel}
                    </div>
                    <div>
                        <p className="text-sm font-semibold">{check.label}</p>
                        <p className="text-[10px] text-muted-foreground">
                            {[
                                check.interval_hours > 0 && `${check.interval_hours}h`,
                                check.interval_cycles > 0 && `${check.interval_cycles} ciclos`,
                                check.interval_days > 0 && `${check.interval_days}d`,
                            ].filter(Boolean).join(' / ') || 'Sin intervalo'}
                        </p>
                    </div>
                </div>
                <Badge className={`${style.bg} ${style.text} text-[10px]`}>{style.label}</Badge>
            </div>
            <ProgressBar value={check.remaining_hours} max={check.interval_hours} label="Horas" unit="h" />
            <ProgressBar value={check.remaining_cycles} max={check.interval_cycles} label="Ciclos" unit="cyc" />
            <ProgressBar value={check.remaining_days} max={check.interval_days} label="Días" unit="d" />
            <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t border-border/30">
                <span>Prox: {check.next_due_date ? new Date(check.next_due_date).toLocaleDateString('es') : '-'}</span>
                <span>
                    {check.interval_hours > 0 && `Due @ ${check.next_due_hours}h`}
                    {check.interval_hours > 0 && check.interval_cycles > 0 && ' / '}
                    {check.interval_cycles > 0 && `${check.next_due_cycles}cyc`}
                </span>
            </div>
            {canManage && (
                <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="flex-1 h-7 text-[10px]" onClick={() => onRecordCheck(check)}
                            data-testid={`record-check-${check.check_type}${isOOP ? '-' + check.program_id : ''}`}>
                        <CheckCircle className="w-3 h-3 mr-1" />Registrar {isOOP ? 'Tarea' : 'Check'}
                    </Button>
                    {canGenerateWO && (
                        <Button size="sm" variant="outline" className="flex-1 h-7 text-[10px] border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                                onClick={() => onGenerateWO(check)}
                                data-testid={`gen-wo-${check.check_type}${isOOP ? '-' + check.program_id : ''}`}>
                            <Wrench className="w-3 h-3 mr-1" />Generar OT
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

function AircraftRow({ ac, onInitialize, onRecordCheck, onGenerateWO, onNewOOP, canManage }) {
    const [expanded, setExpanded] = useState(false);
    const style = alertStyles[ac.worst_alert] || alertStyles.ok;
    const Icon = expanded ? ChevronUp : ChevronDown;

    return (
        <Card className={`card-aviation ${ac.worst_alert === 'overdue' ? 'border-red-500/40' : ac.worst_alert === 'warning' ? 'border-amber-500/40' : ''}`}
              data-testid={`aircraft-mx-${ac.registration}`}>
            <CardContent className="p-0">
                <button className="w-full p-4 flex items-center justify-between text-left" onClick={() => setExpanded(!expanded)}
                        data-testid={`toggle-${ac.registration}`}>
                    <div className="flex items-center gap-3">
                        <div className={`w-2 h-10 rounded-full ${style.dot}`} />
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-primary">{ac.registration}</span>
                                <span className="text-xs text-muted-foreground">{ac.aircraft_type} {ac.model || ''}</span>
                                <Badge variant="outline" className="text-[10px]">{ac.operational_status}</Badge>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-0.5">
                                <span className="font-mono">{ac.total_flight_hours || 0} FH</span>
                                <span className="font-mono">{ac.total_cycles || 0} CYC</span>
                                {ac.has_programs ? (
                                    <span>{ac.checks.length} tareas configuradas</span>
                                ) : (
                                    <span className="text-amber-400">Sin programa de mantenimiento</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        {ac.has_programs && ac.checks.map(c => {
                            const s = alertStyles[c.alert_level];
                            return (
                                <div key={c.program_id} className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold ${s.bg} ${s.text}`}
                                     title={c.label}>
                                    {c.check_type === 'OOP' ? '•' : c.check_type}
                                </div>
                            );
                        })}
                        <Icon className="w-4 h-4 text-muted-foreground ml-2" />
                    </div>
                </button>

                {expanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-border/30 pt-3">
                        {!ac.has_programs ? (
                            <div className="text-center py-4">
                                <Settings className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                                <p className="text-sm text-muted-foreground mb-3">Sin programa de mantenimiento configurado</p>
                                {canManage && (
                                    <Button size="sm" onClick={() => onInitialize(ac.aircraft_id)} data-testid={`init-${ac.registration}`}>
                                        <Plus className="w-4 h-4 mr-1" />Inicializar Programas A/B/C/D
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                                    {ac.checks.map(c => (
                                        <CheckCard key={c.program_id} check={c} onRecordCheck={onRecordCheck} onGenerateWO={onGenerateWO} canManage={canManage} />
                                    ))}
                                </div>
                                {canManage && (
                                    <div className="pt-2 border-t border-border/30">
                                        <Button size="sm" variant="outline" onClick={() => onNewOOP(ac)}
                                                data-testid={`new-oop-${ac.registration}`}>
                                            <Plus className="w-4 h-4 mr-1" />Nueva Tarea OOP (Out of Phase)
                                        </Button>
                                        <p className="text-[10px] text-muted-foreground mt-1">
                                            Tareas fuera de fase: borescope, revisión de tren, botella de oxígeno, etc.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function CalendarTimeline({ events }) {
    const months = {};
    events.forEach(e => {
        if (!months[e.due_month]) months[e.due_month] = [];
        months[e.due_month].push(e);
    });

    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const sortedMonths = Object.keys(months).sort();

    if (sortedMonths.length === 0) {
        return (
            <Card className="card-aviation">
                <CardContent className="py-12 text-center text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>No hay eventos programados en los proximos meses</p>
                    <p className="text-xs mt-1">Configure programas de mantenimiento para ver el calendario</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4" data-testid="calendar-timeline">
            {sortedMonths.map(month => {
                const [y, m] = month.split('-');
                const label = `${monthNames[parseInt(m) - 1]} ${y}`;
                const isPast = new Date(month + '-01') < new Date(new Date().toISOString().slice(0, 7) + '-01');
                return (
                    <div key={month}>
                        <div className="flex items-center gap-2 mb-2">
                            <div className={`h-px flex-1 ${isPast ? 'bg-red-500/30' : 'bg-border'}`} />
                            <span className={`text-xs font-bold uppercase ${isPast ? 'text-red-400' : 'text-muted-foreground'}`}>{label}</span>
                            <div className={`h-px flex-1 ${isPast ? 'bg-red-500/30' : 'bg-border'}`} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {months[month].map(ev => {
                                const style = alertStyles[ev.alert_level];
                                return (
                                    <div key={ev.program_id} className={`rounded-lg border ${style.border} p-3 flex items-center gap-3`}
                                         data-testid={`cal-event-${ev.registration}-${ev.check_type}`}>
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0"
                                             style={{ backgroundColor: checkColors[ev.check_type] + '20', color: checkColors[ev.check_type] }}>
                                            {ev.check_type}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono font-bold text-sm">{ev.registration}</span>
                                                <Badge className={`${style.bg} ${style.text} text-[10px]`}>{style.label}</Badge>
                                            </div>
                                            <p className="text-[10px] text-muted-foreground truncate">{ev.label} - {ev.aircraft_type}</p>
                                            <p className="text-[10px] text-muted-foreground font-mono">
                                                {ev.remaining_hours}h / {ev.remaining_cycles}cyc / {ev.remaining_days}d restantes
                                            </p>
                                        </div>
                                        <div className="text-right text-[10px] text-muted-foreground flex-shrink-0">
                                            <p className="font-mono">{new Date(ev.due_date).toLocaleDateString('es')}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


export default function PredictiveMaintenancePage() {
    const { user, hasRole } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [fleetData, setFleetData] = useState(null);
    const [calendarData, setCalendarData] = useState(null);
    const [activeTab, setActiveTab] = useState('fleet');
    const [recordDialog, setRecordDialog] = useState(null);
    const userFullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || '';
    const [recordForm, setRecordForm] = useState({
        performed_by: '', work_order: '', notes: '',
        closed_by_signature: '', verified_by_signature: '',
        findings: [],
    });
    // Auditoria 6 - Item 14: Nueva tarea OOP (Out of Phase)
    const [oopDialog, setOopDialog] = useState(null); // aircraft seleccionada
    const [oopForm, setOopForm] = useState({
        label: '', description: '',
        interval_hours: 0, interval_cycles: 0, interval_days: 0,
        last_check_date: '', last_check_hours: '', last_check_cycles: '',
    });
    const [savingOOP, setSavingOOP] = useState(false);
    const canManage = hasRole(['admin', 'maintenance']);

    const fetchData = useCallback(async () => {
        try {
            const [statusRes, calRes] = await Promise.all([
                axios.get(`${API}/predictive-maintenance/status`),
                axios.get(`${API}/predictive-maintenance/calendar?months=12`),
            ]);
            setFleetData(statusRes.data);
            setCalendarData(calRes.data);
        } catch {
            toast.error('Error al cargar datos de mantenimiento predictivo');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const initializeAircraft = async (aircraftId) => {
        try {
            await axios.post(`${API}/predictive-maintenance/initialize/${aircraftId}`);
            toast.success('Programas A/B/C/D inicializados');
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al inicializar');
        }
    };

    const handleRecordCheck = async () => {
        if (!recordDialog) return;
        // Auditoria 6 - Item 17: firma obligatoria (usa userFullName por defecto)
        const signature = (recordForm.closed_by_signature || userFullName).trim();
        if (!signature) {
            toast.error('Firma de cierre obligatoria');
            return;
        }
        // Auditoria 6 - Item 16: filtrar findings vacios
        const findings = (recordForm.findings || []).filter(f => (f.description || '').trim().length > 0);
        try {
            const res = await axios.post(`${API}/predictive-maintenance/record-check`, {
                program_id: recordDialog.program_id,
                performed_by: recordForm.performed_by,
                work_order: recordForm.work_order,
                notes: recordForm.notes,
                closed_by_signature: signature,
                verified_by_signature: recordForm.verified_by_signature,
                findings,
            });
            const nMx = res.data?.findings_created?.maintenance_ids?.length || 0;
            const nDef = res.data?.findings_created?.deferral_ids?.length || 0;
            const nAog = res.data?.findings_created?.aog_ids?.length || 0;
            let msg = `${recordDialog.check_type === 'OOP' ? 'Tarea OOP' : 'Check ' + recordDialog.check_type} registrado`;
            if (nMx > 0) msg += ` · ${nMx} tarea(s) de mtto creada(s)`;
            if (nDef > 0) msg += ` · ${nDef} diferido(s) MEL creado(s)`;
            if (nAog > 0) msg += ` · ${nAog} AOG DECLARADO(S)`;
            if (nAog > 0) toast.warning(msg);
            else toast.success(msg);
            setRecordDialog(null);
            setRecordForm({ performed_by: '', work_order: '', notes: '', closed_by_signature: '', verified_by_signature: '', findings: [] });
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al registrar');
        }
    };

    // Auditoria 6 - Item 15 + Auditoria 7 - Item 4: generar OT desde alerta con link directo al registro
    const handleGenerateWO = async (check) => {
        try {
            const res = await axios.post(`${API}/predictive-maintenance/programs/${check.program_id}/generate-work-order`, {});
            const wo = res.data?.work_order;
            const folio = wo?.work_order || wo?.id?.slice(0, 8);
            if (res.data?.reused) {
                toast.info('Ya existe una OT abierta para este check', {
                    action: { label: 'Ver OT', onClick: () => navigate(`/maintenance?wo=${wo?.id || ''}`) },
                });
            } else {
                toast.success(`OT ${folio} generada · prioridad ${wo?.priority?.toUpperCase() || 'MEDIUM'}`, {
                    action: { label: 'Ver OT', onClick: () => navigate(`/maintenance?wo=${wo?.id || ''}`) },
                });
            }
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al generar OT');
        }
    };

    // Auditoria 7 - Item 5: exportar OT en PDF
    const exportWorkOrderPDF = async (maintenanceId) => {
        try {
            const res = await axios.get(`${API}/maintenance/${maintenanceId}/pdf`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (e) {
            toast.error('Error al generar PDF');
        }
    };

    // Auditoria 6 - Item 16: helpers de findings en el diálogo record-check
    const addFinding = () => {
        setRecordForm(prev => ({
            ...prev,
            findings: [...(prev.findings || []), { description: '', resolution_type: 'maintenance_task', priority: 'medium', mel_reference: '', category: 'C' }],
        }));
    };
    const updateFinding = (idx, patch) => {
        setRecordForm(prev => ({
            ...prev,
            findings: prev.findings.map((f, i) => i === idx ? { ...f, ...patch } : f),
        }));
    };
    const removeFinding = (idx) => {
        setRecordForm(prev => ({
            ...prev,
            findings: prev.findings.filter((_, i) => i !== idx),
        }));
    };

    // Auditoria 6 - Item 14: Nueva tarea OOP
    const openOOPDialog = (ac) => {
        setOopDialog(ac);
        setOopForm({
            label: '', description: '',
            interval_hours: 0, interval_cycles: 0, interval_days: 0,
            last_check_date: new Date().toISOString().slice(0, 10),
            last_check_hours: ac.total_flight_hours || 0,
            last_check_cycles: ac.total_cycles || 0,
        });
    };

    const submitOOP = async () => {
        if (!oopDialog) return;
        if (!oopForm.label.trim()) { toast.error('Nombre de la tarea es obligatorio'); return; }
        const ih = parseFloat(oopForm.interval_hours) || 0;
        const ic = parseInt(oopForm.interval_cycles) || 0;
        const id_ = parseInt(oopForm.interval_days) || 0;
        if (ih <= 0 && ic <= 0 && id_ <= 0) {
            toast.error('Al menos un intervalo (horas, ciclos o días) debe ser > 0');
            return;
        }
        setSavingOOP(true);
        try {
            await axios.post(`${API}/predictive-maintenance/programs`, {
                aircraft_id: oopDialog.aircraft_id,
                check_type: 'OOP',
                label: oopForm.label.trim(),
                description: oopForm.description,
                interval_hours: ih,
                interval_cycles: ic,
                interval_days: id_,
                last_check_date: oopForm.last_check_date ? new Date(oopForm.last_check_date).toISOString() : undefined,
                last_check_hours: parseFloat(oopForm.last_check_hours) || 0,
                last_check_cycles: parseInt(oopForm.last_check_cycles) || 0,
            });
            toast.success(`Tarea OOP "${oopForm.label}" creada`);
            setOopDialog(null);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al crear tarea OOP');
        } finally {
            setSavingOOP(false);
        }
    };

    const summary = fleetData?.summary || { overdue: 0, warning: 0, caution: 0, ok: 0 };
    const fleet = fleetData?.fleet || [];
    const events = calendarData?.events || [];

    if (loading) {
        return <div className="space-y-4 animate-fade-in">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-lg skeleton-pulse" />)}</div>;
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="predictive-maintenance-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                        Mantenimiento Predictivo
                    </h1>
                    <p className="text-muted-foreground">
                        Calendario de checks A/B/C/D + tareas OOP basado en horas, ciclos y días
                    </p>
                </div>
                <Button variant="outline" onClick={fetchData} data-testid="refresh-mx">
                    <RefreshCw className="w-4 h-4 mr-2" />Actualizar
                </Button>
            </div>

            {/* Summary KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="card-aviation border-red-500/30">
                    <CardContent className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold text-red-400" data-testid="kpi-overdue">{summary.overdue}</p>
                            <p className="text-[10px] text-muted-foreground uppercase">Vencidos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation border-amber-500/30">
                    <CardContent className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold text-amber-400" data-testid="kpi-warning">{summary.warning}</p>
                            <p className="text-[10px] text-muted-foreground uppercase">Alerta</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation border-yellow-500/30">
                    <CardContent className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-yellow-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold text-yellow-400" data-testid="kpi-caution">{summary.caution}</p>
                            <p className="text-[10px] text-muted-foreground uppercase">Precaucion</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation border-emerald-500/30">
                    <CardContent className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold text-emerald-400" data-testid="kpi-ok">{summary.ok}</p>
                            <p className="text-[10px] text-muted-foreground uppercase">OK</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Info bar */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground bg-muted/30 rounded-lg px-4 py-2">
                <span><strong>{fleetData?.total_aircraft || 0}</strong> aeronaves en flota</span>
                <span className="text-border">|</span>
                <span><strong>{fleetData?.configured_aircraft || 0}</strong> con programa configurado</span>
                <span className="text-border">|</span>
                <span><strong>{events.length}</strong> eventos en calendario</span>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="fleet" data-testid="tab-fleet">
                        <Plane className="w-3.5 h-3.5 mr-1" />Estado de Flota
                    </TabsTrigger>
                    <TabsTrigger value="calendar" data-testid="tab-calendar">
                        <Calendar className="w-3.5 h-3.5 mr-1" />Calendario
                        {summary.overdue > 0 && <Badge variant="destructive" className="ml-1.5 text-[10px] h-4 px-1">{summary.overdue}</Badge>}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="fleet" className="mt-4 space-y-3">
                    {fleet.length === 0 ? (
                        <Card className="card-aviation">
                            <CardContent className="py-12 text-center text-muted-foreground">
                                <Wrench className="w-12 h-12 mx-auto mb-3 opacity-40" />
                                <p>No hay aeronaves en la flota</p>
                            </CardContent>
                        </Card>
                    ) : fleet.map(ac => (
                        <AircraftRow key={ac.aircraft_id} ac={ac}
                            onInitialize={initializeAircraft}
                            onRecordCheck={(check) => { setRecordDialog(check); setRecordForm({ performed_by: '', work_order: '', notes: '', closed_by_signature: userFullName, verified_by_signature: '', findings: [] }); }}
                            onGenerateWO={handleGenerateWO}
                            onNewOOP={openOOPDialog}
                            canManage={canManage} />
                    ))}
                </TabsContent>

                <TabsContent value="calendar" className="mt-4">
                    <CalendarTimeline events={events} />
                </TabsContent>
            </Tabs>

            {/* Record Check Dialog */}
            <Dialog open={!!recordDialog} onOpenChange={(open) => { if (!open) setRecordDialog(null); }}>
                <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto" data-testid="record-check-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">
                            {recordDialog?.check_type === 'OOP' ? 'Registrar Tarea OOP Completada' : `Registrar Check ${recordDialog?.check_type} Completado`}
                        </DialogTitle>
                    </DialogHeader>
                    {recordDialog && (
                        <div className="space-y-3">
                            <div className="p-2 rounded bg-muted/30 text-xs">
                                <span className="font-bold text-primary">{recordDialog.label}</span>
                                <span className="text-muted-foreground"> - Proximo a {recordDialog.next_due_hours}h / {recordDialog.next_due_cycles} ciclos</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Realizado por (MRO / Taller)</Label>
                                    <Input value={recordForm.performed_by} onChange={e => setRecordForm({...recordForm, performed_by: e.target.value})}
                                           placeholder="Ej: AEROMAN, TechOps" data-testid="check-performed-by" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Orden de Trabajo</Label>
                                    <Input value={recordForm.work_order} onChange={e => setRecordForm({...recordForm, work_order: e.target.value})}
                                           placeholder="Ej: WO-2026-001" data-testid="check-work-order" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Notas</Label>
                                <Textarea value={recordForm.notes} onChange={e => setRecordForm({...recordForm, notes: e.target.value})}
                                          placeholder="Observaciones del check..." rows={2} data-testid="check-notes" />
                            </div>

                            {/* Auditoria 6 - Item 17: Firma de cierre */}
                            <div className="pt-2 border-t border-border/30 space-y-2">
                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                    <PenTool className="w-3 h-3" />Firma de cierre
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Cerró el check *</Label>
                                        <Input value={recordForm.closed_by_signature}
                                               onChange={e => setRecordForm({...recordForm, closed_by_signature: e.target.value})}
                                               placeholder={userFullName} data-testid="check-signature-closed" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Verificó (opcional)</Label>
                                        <Input value={recordForm.verified_by_signature}
                                               onChange={e => setRecordForm({...recordForm, verified_by_signature: e.target.value})}
                                               placeholder="Ej: Ing. Ramírez" data-testid="check-signature-verified" />
                                    </div>
                                </div>
                            </div>

                            {/* Auditoria 6 - Item 16: Hallazgos */}
                            <div className="pt-2 border-t border-border/30 space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" />Hallazgos encontrados
                                    </p>
                                    <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addFinding} data-testid="add-finding-btn">
                                        <Plus className="w-3 h-3 mr-1" />Agregar hallazgo
                                    </Button>
                                </div>
                                {(recordForm.findings || []).length === 0 && (
                                    <p className="text-[10px] text-muted-foreground italic">Sin hallazgos. Añade uno si detectaste algo que requiera seguimiento.</p>
                                )}
                                {(recordForm.findings || []).map((f, idx) => (
                                    <div key={idx} className="p-2 rounded border border-amber-500/30 bg-amber-500/5 space-y-2" data-testid={`finding-${idx}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <Textarea value={f.description}
                                                      onChange={e => updateFinding(idx, { description: e.target.value })}
                                                      placeholder="Describe el hallazgo..." rows={2}
                                                      className="text-xs"
                                                      data-testid={`finding-desc-${idx}`} />
                                            <Button size="sm" variant="ghost" onClick={() => removeFinding(idx)} className="text-red-400 h-8 w-8 p-0"
                                                    data-testid={`finding-remove-${idx}`}>
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Select value={f.resolution_type} onValueChange={v => updateFinding(idx, { resolution_type: v })}>
                                                <SelectTrigger className="h-8 text-xs" data-testid={`finding-type-${idx}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="maintenance_task">Tarea de mtto</SelectItem>
                                                    <SelectItem value="mel_deferral">Diferido MEL</SelectItem>
                                                    <SelectItem value="aog">AOG (no diferible)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Select value={f.priority} onValueChange={v => updateFinding(idx, { priority: v })}>
                                                <SelectTrigger className="h-8 text-xs" data-testid={`finding-priority-${idx}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="low">Baja</SelectItem>
                                                    <SelectItem value="medium">Media</SelectItem>
                                                    <SelectItem value="high">Alta</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {f.resolution_type === 'mel_deferral' && (
                                            <div className="grid grid-cols-2 gap-2">
                                                <Input placeholder="MEL Ref (ej. 21-31-01)" value={f.mel_reference || ''}
                                                       onChange={e => updateFinding(idx, { mel_reference: e.target.value })}
                                                       className="h-8 text-xs" data-testid={`finding-mel-${idx}`} />
                                                <Select value={f.category || 'C'} onValueChange={v => updateFinding(idx, { category: v })}>
                                                    <SelectTrigger className="h-8 text-xs" data-testid={`finding-cat-${idx}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="A">Cat A (72h)</SelectItem>
                                                        <SelectItem value="B">Cat B (3 días)</SelectItem>
                                                        <SelectItem value="C">Cat C (10 días)</SelectItem>
                                                        <SelectItem value="D">Cat D (120 días)</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                        {f.resolution_type === 'aog' && (
                                            <div className="grid grid-cols-1 gap-2">
                                                <Input type="datetime-local" placeholder="ETR" value={f.etr || ''}
                                                       onChange={e => updateFinding(idx, { etr: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                                                       className="h-8 text-xs" data-testid={`finding-etr-${idx}`} />
                                                <p className="text-[10px] text-red-400">
                                                    ⚠ AOG declarará la aeronave fuera de servicio y creará un aog_event.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRecordDialog(null)}>Cancelar</Button>
                        <Button onClick={handleRecordCheck} className="btn-primary" data-testid="submit-record-check">
                            <CheckCircle className="w-4 h-4 mr-1" />Registrar y firmar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Nueva Tarea OOP Dialog (Auditoria 6 - Item 14) */}
            <Dialog open={!!oopDialog} onOpenChange={(open) => { if (!open) setOopDialog(null); }}>
                <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto" data-testid="oop-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">
                            Nueva Tarea OOP · {oopDialog?.registration}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="p-2 rounded bg-cyan-500/10 border border-cyan-500/30 text-xs text-cyan-300">
                            Tareas <strong>Out of Phase</strong>: seguimiento fuera del ciclo A/B/C/D (ej. borescope, tren de aterrizaje, botella de oxígeno).
                            Define al menos un intervalo (horas, ciclos o días).
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Nombre de la tarea *</Label>
                            <Input value={oopForm.label} onChange={e => setOopForm({...oopForm, label: e.target.value})}
                                   placeholder="Ej: Borescope motor izq, LG overhaul, Rev. oxígeno" data-testid="oop-label" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Descripción</Label>
                            <Textarea value={oopForm.description} onChange={e => setOopForm({...oopForm, description: e.target.value})}
                                      placeholder="Detalle opcional de la tarea" rows={2} data-testid="oop-description" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Intervalo horas</Label>
                                <Input type="number" min="0" value={oopForm.interval_hours}
                                       onChange={e => setOopForm({...oopForm, interval_hours: e.target.value})}
                                       data-testid="oop-interval-hours" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Intervalo ciclos</Label>
                                <Input type="number" min="0" value={oopForm.interval_cycles}
                                       onChange={e => setOopForm({...oopForm, interval_cycles: e.target.value})}
                                       data-testid="oop-interval-cycles" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Intervalo días</Label>
                                <Input type="number" min="0" value={oopForm.interval_days}
                                       onChange={e => setOopForm({...oopForm, interval_days: e.target.value})}
                                       data-testid="oop-interval-days" />
                            </div>
                        </div>
                        <div className="pt-2 border-t border-border/30">
                            <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-widest">Última realización (opcional)</p>
                            <div className="grid grid-cols-3 gap-2">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Fecha</Label>
                                    <Input type="date" value={oopForm.last_check_date}
                                           onChange={e => setOopForm({...oopForm, last_check_date: e.target.value})}
                                           data-testid="oop-last-date" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Horas AC</Label>
                                    <Input type="number" min="0" value={oopForm.last_check_hours}
                                           onChange={e => setOopForm({...oopForm, last_check_hours: e.target.value})}
                                           data-testid="oop-last-hours" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Ciclos AC</Label>
                                    <Input type="number" min="0" value={oopForm.last_check_cycles}
                                           onChange={e => setOopForm({...oopForm, last_check_cycles: e.target.value})}
                                           data-testid="oop-last-cycles" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOopDialog(null)}>Cancelar</Button>
                        <Button onClick={submitOOP} disabled={savingOOP} className="btn-primary" data-testid="submit-oop">
                            <Plus className="w-4 h-4 mr-1" />Crear Tarea OOP
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
